import React from 'react'

/**
 * Marca oficial "ap." — Atividade Pronta.
 *
 * SVG inline lendo CSS custom properties (--logo-*, definidas em
 * design/Caderno/tokens.css). O modo claro/escuro flui pelos seletores
 * `.dark` / `[data-theme="dark"]` já existentes — sem JavaScript de tema.
 *
 * Regras de uso (handoff: design_handoff_identidade_visual/README.md):
 * - símbolo nunca abaixo de 24px; favicon (≤16px) é asset próprio, sem o ponto
 * - área de respiro mínima de 4× o diâmetro do ponto ao redor da marca
 * - wordmark é texto vivo em Fraunces com ponto final — nunca imagem
 * - proibido: gradiente, rotação, distorção, cores fora da paleta
 */

export type LogoVariant = 'symbol' | 'lockup' | 'lockup-vertical' | 'wordmark'

export interface LogoProps {
  variant?: LogoVariant
  /**
   * symbol / lockup / lockup-vertical: altura do símbolo em px (mínimo 24).
   * wordmark: font-size do texto em px (sem mínimo — uso composicional, ex. sidebar).
   */
  size?: number
  /** Força a paleta escura da marca em superfícies escuras nos dois temas (ex.: footer ink-900). */
  onDark?: boolean
  /**
   * Marca o símbolo como decorativo (aria-hidden) quando o nome já é anunciado
   * por texto adjacente — ex.: composição símbolo + wordmark na sidebar.
   * Só tem efeito na variante `symbol`; nos lockups o SVG já é decorativo.
   */
  decorative?: boolean
  className?: string
}

// Razões extraídas dos SVGs canônicos do handoff:
//   - ap-lockup-horizontal.svg: símbolo 64, wordmark font-size 34, gap 18 (x=82)
//   - ap-lockup-vertical.svg:   símbolo 76, wordmark font-size 24
const SYMBOL_MIN_PX = 24
const LOCKUP_GAP_RATIO = 18 / 64
const LOCKUP_WORD_RATIO = 34 / 64
const LOCKUP_V_WORD_RATIO = 24 / 76
const LOCKUP_V_GAP_RATIO = 24 / 76

function guardSymbolSize(size: number): number {
  if (size < SYMBOL_MIN_PX) {
    if (import.meta.env.DEV) {
      console.warn(
        `[Logo] símbolo ${size}px < mínimo 24px ` +
          '(regra inviolável nº 2 do handoff); usando 24px.'
      )
    }
    return SYMBOL_MIN_PX
  }
  return size
}

const ARIA_LABEL = 'Atividade Pronta'

interface SymbolProps {
  size: number
  /** Nos lockups o wordmark (texto vivo) já carrega o nome — o SVG vira decorativo. */
  decorative?: boolean
}

function LogoSymbol({ size, decorative = false }: SymbolProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className="shrink-0"
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': ARIA_LABEL })}
    >
      <rect
        x="1"
        y="1"
        width="62"
        height="62"
        rx="16"
        fill="var(--logo-tile)"
        stroke="var(--logo-tile-line)"
        strokeWidth="1.5"
      />
      <text
        x="29"
        y="33"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Fraunces, Georgia, serif"
        fontWeight="500"
        fontStyle="italic"
        fontSize="34"
        letterSpacing="-1.5"
        fill="var(--logo-letters)"
      >
        ap
      </text>
      <circle cx="49" cy="44" r="3.6" fill="var(--logo-dot)" />
    </svg>
  )
}

function LogoWordmark({ fontSize }: { fontSize: number }) {
  return (
    <span className="logo-wordmark" style={{ fontSize, lineHeight: 1 }}>
      Atividade <em>Pronta.</em>
    </span>
  )
}

const Logo: React.FC<LogoProps> = ({
  variant = 'lockup',
  size = 40,
  onDark = false,
  decorative = false,
  className = '',
}) => {
  const classes = (base: string) =>
    [base, onDark ? 'logo-on-dark' : '', className].filter(Boolean).join(' ')

  if (variant === 'wordmark') {
    return (
      <span className={classes('inline-flex items-center')}>
        <LogoWordmark fontSize={size} />
      </span>
    )
  }

  const symbolSize = guardSymbolSize(size)

  if (variant === 'symbol') {
    return (
      <span className={classes('inline-flex')}>
        <LogoSymbol size={symbolSize} decorative={decorative} />
      </span>
    )
  }

  if (variant === 'lockup-vertical') {
    return (
      <span
        className={classes('inline-flex flex-col items-center')}
        style={{ gap: symbolSize * LOCKUP_V_GAP_RATIO }}
      >
        <LogoSymbol size={symbolSize} decorative />
        <LogoWordmark fontSize={symbolSize * LOCKUP_V_WORD_RATIO} />
      </span>
    )
  }

  return (
    <span
      className={classes('inline-flex items-center')}
      style={{ gap: symbolSize * LOCKUP_GAP_RATIO }}
    >
      <LogoSymbol size={symbolSize} decorative />
      <LogoWordmark fontSize={symbolSize * LOCKUP_WORD_RATIO} />
    </span>
  )
}

export default Logo
